import { useState, useEffect, useCallback, useRef } from "react";

// ─── Constants ──────────────────────────────────────────────────────────────
const API_BASE = "https://api-project360-insights.haqdarshak.com";
const STANDARD_IDS = new Set([0, 1, 4, 5, 7, 8, 9, 10, 11, 13]);
const ROLE_OPTIONS = Array.from({ length: 50 }, (_, i) => ({
  value: i + 1,
  label: `Role ${i + 1}`,
}));

const VIEW_META = {
  "Project Summary":     { icon: "📊", accent: "#3b82f6" },
  "KPI Trends":          { icon: "📈", accent: "#6366f1" },
  "Financial Metrics":   { icon: "💰", accent: "#f59e0b" },
  "Team Overview":       { icon: "👥", accent: "#8b5cf6" },
  "Milestone Status":    { icon: "🏁", accent: "#10b981" },
  "Budget Report":       { icon: "📋", accent: "#f97316" },
  "Regional Map":        { icon: "🗺️", accent: "#14b8a6" },
  "Risk Dashboard":      { icon: "🛡️", accent: "#f43f5e" },
  "Executive Summary":   { icon: "📌", accent: "#a855f7" },
  "Timeline View":       { icon: "📅", accent: "#06b6d4" },
  "Resource Allocation": { icon: "🔧", accent: "#84cc16" },
  "Risk Heatmap":        { icon: "⚠️", accent: "#ef4444" },
  "Vendor Tracker":      { icon: "🏢", accent: "#64748b" },
  "Scope Changelogger":  { icon: "📝", accent: "#0ea5e9" },
  default:               { icon: "📄", accent: "#94a3b8" },
};
const vmeta = name => VIEW_META[name] || VIEW_META.default;

// ─── API ─────────────────────────────────────────────────────────────────────
async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
  return body;
}

// Normalize role_ids — DB may return a JSON string, array, or null
function normalizeRoleIds(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch { return []; }
  }
  return [];
}

// ─── Badge ────────────────────────────────────────────────────────────────────
function Badge({ children, color = "slate" }) {
  const map = {
    slate:  { bg: "#f1f5f9", text: "#475569" },
    blue:   { bg: "#dbeafe", text: "#1d4ed8" },
    green:  { bg: "#dcfce7", text: "#15803d" },
    red:    { bg: "#fee2e2", text: "#b91c1c" },
    amber:  { bg: "#fef9c3", text: "#92400e" },
    purple: { bg: "#ede9fe", text: "#6d28d9" },
    dark:   { bg: "#0f172a", text: "#fff"    },
    teal:   { bg: "#ccfbf1", text: "#0f766e" },
    indigo: { bg: "#e0e7ff", text: "#3730a3" },
  };
  const c = map[color] || map.slate;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "2px 9px", borderRadius: 20,
      fontSize: 11, fontWeight: 700, letterSpacing: 0.2,
      background: c.bg, color: c.text, whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  const ok = type === "success";
  return (
    <div style={{
      position: "fixed", bottom: 28, right: 28, zIndex: 9999,
      display: "flex", alignItems: "center", gap: 10,
      padding: "13px 18px", borderRadius: 14, fontSize: 13, fontWeight: 600,
      boxShadow: "0 12px 40px rgba(0,0,0,0.14)",
      background: ok ? "#f0fdf4" : "#fff1f2",
      color: ok ? "#15803d" : "#b91c1c",
      border: `1.5px solid ${ok ? "#bbf7d0" : "#fecdd3"}`,
      animation: "toastIn .25s cubic-bezier(.34,1.56,.64,1)",
    }}>
      <span style={{ fontSize: 17 }}>{ok ? "✓" : "✕"}</span>
      {message}
      <button onClick={onClose} style={{ marginLeft: 6, opacity: 0.45, background: "none", border: "none", cursor: "pointer", fontSize: 15 }}>✕</button>
    </div>
  );
}

// ─── Searchable PID Select ────────────────────────────────────────────────────
function PidSelect({ label, options, value, onChange, placeholder = "Search or select PID…" }) {
  const [open, setOpen]       = useState(false);
  const [query, setQuery]     = useState("");
  const ref                   = useRef(null);
  const inputRef              = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtered = query.trim()
    ? options.filter(o => o != null && String(o).toLowerCase().includes(query.trim().toLowerCase()))
    : options.filter(o => o != null);

  const handleOpen = () => {
    setOpen(true);
    setQuery("");
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const select = pid => {
    onChange(pid);
    setOpen(false);
    setQuery("");
  };

  const clear = e => {
    e.stopPropagation();
    onChange("");
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {label && (
        <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 6 }}>
          {label}
        </label>
      )}
      <div
        onClick={handleOpen}
        style={{
          width: "100%", display: "flex", alignItems: "center",
          padding: "10px 14px", borderRadius: 10, cursor: "pointer",
          border: `1.5px solid ${open ? "#0f172a" : "#e2e8f0"}`,
          background: "#fff", transition: "border-color .15s", gap: 8,
        }}
      >
        {open ? (
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onClick={e => e.stopPropagation()}
            placeholder={placeholder}
            style={{
              flex: 1, border: "none", outline: "none", fontSize: 13, fontWeight: 600,
              color: "#0f172a", background: "transparent",
            }}
          />
        ) : (
          <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: value ? "#0f172a" : "#94a3b8", fontFamily: value ? "monospace" : "inherit" }}>
            {value || placeholder}
          </span>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
          {value && !open && (
            <button
              onClick={clear}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 14, padding: "0 2px", lineHeight: 1 }}
            >✕</button>
          )}
          <span style={{ fontSize: 10, color: "#94a3b8", transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }}>▼</span>
        </div>
      </div>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 200,
          background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 12,
          boxShadow: "0 8px 30px rgba(0,0,0,0.12)", overflow: "hidden",
          maxHeight: 280, display: "flex", flexDirection: "column",
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: "16px 14px", fontSize: 13, color: "#94a3b8", textAlign: "center" }}>
              No PIDs match "{query}"
            </div>
          ) : (
            <div style={{ overflowY: "auto" }}>
              {filtered.map(pid => (
                <div
                  key={pid}
                  onClick={() => select(pid)}
                  style={{
                    padding: "9px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600,
                    fontFamily: "monospace", color: pid === value ? "#0369a1" : "#334155",
                    background: pid === value ? "#f0f9ff" : "transparent",
                    borderLeft: pid === value ? "3px solid #0ea5e9" : "3px solid transparent",
                    transition: "background .1s",
                  }}
                  onMouseEnter={e => { if (pid !== value) e.currentTarget.style.background = "#f8fafc"; }}
                  onMouseLeave={e => { if (pid !== value) e.currentTarget.style.background = "transparent"; }}
                >
                  {pid}
                </div>
              ))}
            </div>
          )}
          <div style={{ padding: "6px 14px", borderTop: "1px solid #f1f5f9", background: "#f8fafc" }}>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>
              {filtered.length} of {options.length} PIDs
              {query && ` matching "${query}"`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Multi-select dropdown ────────────────────────────────────────────────────
function MultiSelect({ label, options, selected, onChange, placeholder = "Select…", single = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = val => {
    if (single) { onChange([val]); setOpen(false); return; }
    onChange(selected.includes(val) ? selected.filter(x => x !== val) : [...selected, val]);
  };
  const isAll = options.length > 0 && options.every(o => selected.includes(o.value));
  const toggleAll = () => onChange(isAll ? [] : options.map(o => o.value));

  const displayText = selected.length === 0
    ? placeholder
    : single
    ? options.find(o => o.value === selected[0])?.label || placeholder
    : selected.length === options.length
    ? "All selected"
    : selected.length === 1
    ? options.find(o => o.value === selected[0])?.label
    : `${selected.length} selected`;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {label && (
        <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 6 }}>
          {label}
        </label>
      )}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "10px 14px", borderRadius: 10,
          border: `1.5px solid ${open ? "#0f172a" : "#e2e8f0"}`,
          background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600,
          color: selected.length ? "#0f172a" : "#94a3b8",
          transition: "border-color .15s",
        }}>
        <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {displayText}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          {!single && selected.length > 0 && (
            <span style={{ fontSize: 10, fontWeight: 800, background: "#0f172a", color: "#fff", padding: "1px 6px", borderRadius: 10 }}>
              {selected.length}
            </span>
          )}
          <span style={{ fontSize: 10, color: "#94a3b8", transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }}>▼</span>
        </div>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 100,
          background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 12,
          boxShadow: "0 8px 30px rgba(0,0,0,0.10)", overflow: "hidden",
          maxHeight: 260, display: "flex", flexDirection: "column",
        }}>
          {!single && options.length > 3 && (
            <div style={{ padding: "8px 12px", borderBottom: "1px solid #f1f5f9" }}>
              <button onClick={toggleAll} style={{ fontSize: 12, fontWeight: 700, color: "#3b82f6", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                {isAll ? "Deselect all" : "Select all"}
              </button>
            </div>
          )}
          <div style={{ overflowY: "auto" }}>
            {options.map(opt => {
              const sel = selected.includes(opt.value);
              return (
                <div
                  key={opt.value}
                  onClick={() => toggle(opt.value)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "9px 14px", cursor: "pointer", fontSize: 13, fontWeight: 500,
                    background: sel ? "#f0f9ff" : "transparent",
                    color: sel ? "#0369a1" : "#334155",
                  }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = "#f8fafc"; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = "transparent"; }}
                >
                  <span style={{
                    width: 16, height: 16, borderRadius: single ? "50%" : 4, flexShrink: 0,
                    border: `2px solid ${sel ? "#0ea5e9" : "#cbd5e1"}`,
                    background: sel ? "#0ea5e9" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {sel && <span style={{ color: "#fff", fontSize: 9, fontWeight: 900 }}>✓</span>}
                  </span>
                  {opt.icon && <span style={{ fontSize: 14 }}>{opt.icon}</span>}
                  <span style={{ flex: 1 }}>{opt.label}</span>
                  {opt.badge && <Badge color={opt.badgeColor || "slate"}>{opt.badge}</Badge>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Section header ───────────────────────────────────────────────────────────
function SectionHead({ icon, title, sub, right }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18, gap: 12 }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
          {icon && <span style={{ fontSize: 18 }}>{icon}</span>}
          <h2 style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", margin: 0 }}>{title}</h2>
        </div>
        {sub && <p style={{ fontSize: 12, color: "#94a3b8", margin: 0 }}>{sub}</p>}
      </div>
      {right}
    </div>
  );
}

// ─── View toggle chip (Configure panel) ──────────────────────────────────────
function ViewChip({ view, state, onClick }) {
  const cfg = {
    on:      { bg: "#0f172a", border: "#0f172a", text: "#fff",    check: true  },
    off:     { bg: "#fff",    border: "#e2e8f0", text: "#94a3b8", check: false },
    added:   { bg: "#f0fdf4", border: "#86efac", text: "#15803d", check: true  },
    removed: { bg: "#fff5f5", border: "#fca5a5", text: "#dc2626", check: false },
  };
  const c = cfg[state] || cfg.off;
  const meta = vmeta(view.view_name);
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "9px 13px", borderRadius: 10,
        border: `1.5px solid ${c.border}`, background: c.bg, color: c.text,
        cursor: "pointer", textAlign: "left", width: "100%",
        fontSize: 13, fontWeight: 600, transition: "all .15s",
      }}>
      <span style={{
        width: 18, height: 18, borderRadius: 5, flexShrink: 0,
        border: `2px solid ${c.check ? "transparent" : "#d1d5db"}`,
        background: c.check ? (state === "added" ? "#16a34a" : "#fff") : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {c.check && <span style={{ color: state === "added" ? "#fff" : "#0f172a", fontSize: 10, fontWeight: 900 }}>✓</span>}
      </span>
      <span style={{ fontSize: 15, flexShrink: 0 }}>{meta.icon}</span>
      <span style={{ flex: 1, textDecoration: state === "removed" ? "line-through" : "none" }}>{view.view_name}</span>
      {STANDARD_IDS.has(view.id) && state !== "added" && (
        <Badge color={state === "on" ? "dark" : "slate"}>STD</Badge>
      )}
      {state === "added"   && <Badge color="green">+</Badge>}
      {state === "removed" && <Badge color="red">–</Badge>}
    </button>
  );
}

// ─── Config table ─────────────────────────────────────────────────────────────
function ConfigTable({ config, allViews, onEdit }) {
  const viewById = Object.fromEntries(allViews.map(v => [v.id, v]));
  const stdActive  = (config.standard_view_ids || []).map(id => viewById[id]).filter(Boolean);
  const custActive = (config.custom_view_ids   || []).map(id => viewById[id]).filter(Boolean);
  const removed    = [...STANDARD_IDS]
    .filter(id => !(config.standard_view_ids || []).includes(id))
    .map(id => viewById[id]).filter(Boolean);
  const total = stdActive.length + custActive.length;

  const thS = {
    padding: "9px 16px", fontSize: 11, fontWeight: 700, color: "#94a3b8",
    letterSpacing: 0.7, textTransform: "uppercase", textAlign: "left",
    background: "#f8fafc", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap",
  };
  const tdS = {
    padding: "11px 16px", fontSize: 13, color: "#1e293b",
    borderBottom: "1px solid #f1f5f9", verticalAlign: "middle",
  };

  const rows = [
    ...stdActive.map(v => ({ v, type: "std",    status: "active"  })),
    ...custActive.map(v => ({ v, type: "custom", status: "added"   })),
    ...removed.map(v =>    ({ v, type: "std",    status: "removed" })),
  ];

  return (
    <div style={{ background: "#fff", borderRadius: 16, border: "1.5px solid #e2e8f0", overflow: "hidden" }}>
      <div style={{
        padding: "16px 20px", borderBottom: "1px solid #e2e8f0", background: "#fafafa",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
      }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
            <span style={{ fontSize: 16 }}>📋</span>
            <span style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>Saved Configuration</span>
          </div>
          <p style={{ fontSize: 12, color: "#94a3b8", margin: 0 }}>
            <span style={{ fontFamily: "monospace", fontWeight: 700, color: "#475569" }}>{config.pid}</span>
            &nbsp;·&nbsp;Group <strong style={{ color: "#475569" }}>{config.created_groups}</strong>
          </p>
        </div>
        <button
          onClick={onEdit}
          style={{
            display: "flex", alignItems: "center", gap: 7,
            padding: "9px 18px", borderRadius: 9, border: "none",
            background: "#0f172a", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}>
          ✏️ Edit
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", borderBottom: "1px solid #e2e8f0" }}>
        {[
          ["Total",      total,             "#0f172a", "#f8fafc"],
          ["Std Active", stdActive.length,  "#1d4ed8", "#eff6ff"],
          ["Custom",     custActive.length, "#15803d", "#f0fdf4"],
          ["Removed",    removed.length,    "#dc2626", "#fff5f5"],
        ].map(([lbl, val, col, bg], i) => (
          <div key={lbl} style={{
            padding: "12px 8px", background: bg, textAlign: "center",
            borderRight: i < 3 ? "1px solid #e2e8f0" : "none",
          }}>
            <p style={{ fontSize: 20, fontWeight: 800, color: col, margin: 0 }}>{val}</p>
            <p style={{ fontSize: 11, color: "#94a3b8", margin: "2px 0 0", fontWeight: 600 }}>{lbl}</p>
          </div>
        ))}
      </div>

      {rows.length === 0 ? (
        <div style={{ padding: "40px 24px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>No views configured</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...thS, width: 44 }}>#</th>
                <th style={thS}>View</th>
                <th style={thS}>View ID</th>
                <th style={thS}>Type</th>
                <th style={thS}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ v, type, status }, i) => {
                const isRemoved = status === "removed";
                const meta = vmeta(v.view_name);
                const activeRows = rows.filter(r => r.status !== "removed");
                const activeIndex = activeRows.indexOf(rows[i]);
                return (
                  <tr key={`${type}-${v.id}`} style={{ background: isRemoved ? "#fff8f8" : i % 2 === 0 ? "#fff" : "#fafafa" }}>
                    <td style={{ ...tdS, fontWeight: 800, color: isRemoved ? "#f87171" : "#94a3b8", fontSize: 12 }}>
                      {isRemoved ? "—" : activeIndex + 1}
                    </td>
                    <td style={tdS}>
                      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                        <span style={{ fontSize: 17, opacity: isRemoved ? 0.35 : 1 }}>{meta.icon}</span>
                        <span style={{ fontWeight: 600, color: isRemoved ? "#ef4444" : "#1e293b", textDecoration: isRemoved ? "line-through" : "none" }}>
                          {v.view_name}
                        </span>
                      </div>
                    </td>
                    <td style={{ ...tdS, fontFamily: "monospace", fontSize: 12, color: "#64748b" }}>{v.id}</td>
                    <td style={tdS}>
                      {type === "custom"
                        ? <Badge color="green">Custom</Badge>
                        : <Badge color={isRemoved ? "red" : "blue"}>Standard</Badge>}
                    </td>
                    <td style={tdS}>
                      {status === "active"  && <Badge color="green">✓ Active</Badge>}
                      {status === "added"   && <Badge color="purple">+ Added</Badge>}
                      {status === "removed" && <Badge color="red">✕ Removed</Badge>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: HOME
// ═══════════════════════════════════════════════════════════════════════════════
function HomeTab({ pids, groups, selectedPid, setSelectedPid, selectedGroups, setSelectedGroups,
  configLoaded, loading, onLoad, existingConfig, allViews, onEdit }) {

  const canLoad = !!selectedPid && selectedGroups.length === 1;

  const groupOptions = groups.map(g => ({ value: g, label: g }));

  return (
    <div style={{ maxWidth: 900 }}>
      <SectionHead
        icon="🏠"
        title="Load Dashboard Configuration"
        sub="Select a project and group to view or create its access configuration."
      />

      <div style={{
        background: "#fff", borderRadius: 16, border: "1.5px solid #e2e8f0",
        padding: "20px 22px", marginBottom: 20,
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 16, alignItems: "flex-end" }}>
          {/* Searchable PID select */}
          <PidSelect
            label="Project (PID)"
            options={pids}
            value={selectedPid}
            onChange={pid => { setSelectedPid(pid); setSelectedGroups([]); }}
          />
          <MultiSelect
            label="Group"
            options={groupOptions}
            selected={selectedGroups}
            onChange={setSelectedGroups}
            placeholder={groups.length === 0 ? "No groups — create one first" : "— Select Group —"}
            single
          />
          <button
            onClick={onLoad}
            disabled={!canLoad || loading}
            style={{
              display: "flex", alignItems: "center", gap: 8, padding: "10px 22px",
              borderRadius: 10, border: "none", whiteSpace: "nowrap",
              cursor: canLoad && !loading ? "pointer" : "not-allowed",
              background: canLoad && !loading ? "#0f172a" : "#e2e8f0",
              color: canLoad && !loading ? "#fff" : "#94a3b8",
              fontSize: 13, fontWeight: 700, transition: "all .15s",
            }}>
            {loading
              ? <><span style={{ display: "inline-block", animation: "spin 1s linear infinite" }}>⟳</span> Loading…</>
              : <>→ Load</>}
          </button>
        </div>

        {canLoad && (
          <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
            <Badge color="slate">PID: {selectedPid}</Badge>
            {selectedGroups[0] && <Badge color="teal">Group: {selectedGroups[0]}</Badge>}
            <Badge color="blue">{STANDARD_IDS.size} standard views</Badge>
          </div>
        )}

        {groups.length === 0 && (
          <div style={{
            marginTop: 14, padding: "10px 14px", borderRadius: 9,
            background: "#fefce8", border: "1px solid #fde68a", fontSize: 12, color: "#92400e",
          }}>
            ⚠️ No groups exist yet. Go to <strong>Managed Groups</strong> to create one before loading a configuration.
          </div>
        )}
      </div>

      {configLoaded && existingConfig && (
        <ConfigTable config={existingConfig} allViews={allViews} onEdit={onEdit} />
      )}

      {configLoaded && !existingConfig && (
        <div style={{
          background: "#fff", borderRadius: 16, border: "1.5px dashed #cbd5e1",
          padding: "44px 24px", textAlign: "center", animation: "fadeIn .3s ease",
        }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📭</div>
          <p style={{ fontSize: 15, fontWeight: 700, color: "#334155", margin: "0 0 6px" }}>No configuration found</p>
          <p style={{ fontSize: 13, color: "#94a3b8", margin: "0 0 20px" }}>
            No saved config for <strong style={{ color: "#475569" }}>{selectedPid}</strong> · Group {selectedGroups[0]}.
          </p>
          <button onClick={onEdit} style={{
            padding: "10px 22px", borderRadius: 10, border: "none",
            background: "#0f172a", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}>
            ✏️ Create Configuration
          </button>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: CONFIGURE
// ═══════════════════════════════════════════════════════════════════════════════
function ConfigureTab({
  selectedPid, selectedGroup,
  standardViews, customViews,
  selStdIds, selCstIds,
  toggleStd, toggleCst,
  additions, removals, finalViews,
  onPreview,
}) {
  const [panel, setPanel] = useState("all");

  const visibleStd = panel === "custom" ? [] : standardViews;
  const visibleCst = panel === "standard" ? [] : customViews;

  const panelBtn = (id, label) => (
    <button key={id} onClick={() => setPanel(id)} style={{
      padding: "5px 12px", borderRadius: 8, border: "none", cursor: "pointer",
      fontSize: 12, fontWeight: 700,
      background: panel === id ? "#0f172a" : "transparent",
      color: panel === id ? "#fff" : "#64748b",
      transition: "all .12s",
    }}>{label}</button>
  );

  const col = { background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden" };
  const colHead = (label, sub) => (
    <div style={{ padding: "12px 15px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc" }}>
      <p style={{ fontSize: 11, fontWeight: 800, color: "#64748b", letterSpacing: 0.8, textTransform: "uppercase", margin: 0 }}>{label}</p>
      {sub && <p style={{ fontSize: 11, color: "#94a3b8", margin: "2px 0 0" }}>{sub}</p>}
    </div>
  );
  const scrollBox = { padding: 10, display: "flex", flexDirection: "column", gap: 5, maxHeight: 420, overflowY: "auto" };

  return (
    <div>
      <div style={{
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10,
        background: "#fff", borderRadius: 12, border: "1.5px solid #e2e8f0",
        padding: "11px 16px", marginBottom: 16, fontSize: 13,
      }}>
        <span style={{ color: "#64748b" }}>Project</span>
        <Badge color="dark">{selectedPid}</Badge>
        <span style={{ color: "#cbd5e1" }}>·</span>
        <span style={{ color: "#64748b" }}>Group</span>
        <Badge color="teal">{selectedGroup}</Badge>
        <span style={{ color: "#cbd5e1" }}>·</span>
        {additions.length > 0 && <Badge color="green">+{additions.length} added</Badge>}
        {removals.length  > 0 && <Badge color="red">–{removals.length} removed</Badge>}
        {additions.length === 0 && removals.length === 0 && <span style={{ fontSize: 12, color: "#94a3b8" }}>No changes yet</span>}
        <span style={{ marginLeft: "auto" }}>
          <Badge color="blue">{finalViews.length} final views</Badge>
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
        {/* LEFT */}
        <div style={col}>
          {colHead("Available Visuals", "Click to toggle")}
          <div style={{ padding: "8px 10px 0", display: "flex", gap: 4 }}>
            {[["all","All"],["standard","Std"],["custom","Custom"]].map(([id,lbl]) => panelBtn(id, lbl))}
          </div>
          <div style={scrollBox}>
            {visibleStd.length > 0 && (
              <>
                {panel === "all" && <p style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.6, textTransform: "uppercase", padding: "4px 2px 2px" }}>Standard</p>}
                {visibleStd.map(v => (
                  <ViewChip key={v.id} view={v}
                    state={selStdIds.includes(v.id) ? "on" : "removed"}
                    onClick={() => toggleStd(v.id)}
                  />
                ))}
              </>
            )}
            {visibleCst.length > 0 && (
              <>
                {panel === "all" && <p style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.6, textTransform: "uppercase", padding: "8px 2px 2px" }}>Custom</p>}
                {visibleCst.map(v => (
                  <ViewChip key={v.id} view={v}
                    state={selCstIds.includes(v.id) ? "added" : "off"}
                    onClick={() => toggleCst(v.id)}
                  />
                ))}
              </>
            )}
          </div>
        </div>

        {/* MIDDLE */}
        <div style={col}>
          {colHead("Template + Overrides")}
          <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 5, maxHeight: 230, overflowY: "auto", borderBottom: "1px solid #f1f5f9" }}>
            {standardViews.map(v => {
              const gone = !selStdIds.includes(v.id);
              return (
                <div key={v.id} style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "8px 11px",
                  borderRadius: 9, border: `1px solid ${gone ? "#fca5a5" : "#e2e8f0"}`,
                  background: gone ? "#fff5f5" : "#f8fafc",
                }}>
                  <span style={{ fontSize: 14, opacity: gone ? 0.3 : 1 }}>{vmeta(v.view_name).icon}</span>
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: gone ? "#dc2626" : "#64748b", textDecoration: gone ? "line-through" : "none" }}>{v.view_name}</span>
                  {gone && <Badge color="red">DEL</Badge>}
                </div>
              );
            })}
            {additions.map((v, i) => (
              <div key={v.id} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "8px 11px",
                borderRadius: 9, border: "1px solid #86efac", background: "#f0fdf4",
              }}>
                <span style={{ fontSize: 14 }}>{vmeta(v.view_name).icon}</span>
                <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "#15803d" }}>{v.view_name}</span>
                <Badge color="green">ADD·{selStdIds.length + i + 1}</Badge>
              </div>
            ))}
            {standardViews.length === 0 && additions.length === 0 && (
              <p style={{ fontSize: 12, color: "#94a3b8", textAlign: "center", padding: "14px 0" }}>None</p>
            )}
          </div>

          <div style={{ padding: "8px 10px 4px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc" }}>
            <p style={{ fontSize: 10, fontWeight: 800, color: "#64748b", letterSpacing: 0.7, textTransform: "uppercase", margin: 0 }}>
              Final List <span style={{ fontWeight: 400, color: "#94a3b8" }}>({finalViews.length})</span>
            </p>
          </div>
          <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 5, maxHeight: 200, overflowY: "auto" }}>
            {finalViews.length === 0 && <p style={{ fontSize: 12, color: "#94a3b9", textAlign: "center", padding: "12px 0" }}>Empty</p>}
            {finalViews.map((v, i) => {
              const isAdded = additions.some(a => a.id === v.id);
              return (
                <div key={v.id} style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "7px 11px",
                  borderRadius: 9, background: isAdded ? "#f0fdf4" : "#fff",
                  border: `1px solid ${isAdded ? "#86efac" : "#f1f5f9"}`,
                }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: 7, flexShrink: 0,
                    background: "#0f172a", color: "#fff", display: "flex",
                    alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800,
                  }}>{i + 1}</span>
                  <span style={{ fontSize: 13 }}>{vmeta(v.view_name).icon}</span>
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "#334155" }}>{v.view_name}</span>
                  {isAdded && <Badge color="green">New</Badge>}
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT */}
        <div style={col}>
          {colHead("Override Summary")}
          <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <p style={{ fontSize: 11, fontWeight: 800, color: "#16a34a", letterSpacing: 0.7, textTransform: "uppercase", marginBottom: 8 }}>
                + Additions ({additions.length})
              </p>
              {additions.length === 0
                ? <p style={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>None</p>
                : additions.map(v => (
                  <div key={v.id} style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "8px 11px",
                    borderRadius: 9, border: "1px solid #86efac", background: "#f0fdf4", marginBottom: 5,
                  }}>
                    <span style={{ fontSize: 14 }}>{vmeta(v.view_name).icon}</span>
                    <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "#15803d" }}>{v.view_name}</span>
                    <button onClick={() => toggleCst(v.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#86efac", fontSize: 14, padding: 2 }}>✕</button>
                  </div>
                ))
              }
            </div>
            <div>
              <p style={{ fontSize: 11, fontWeight: 800, color: "#dc2626", letterSpacing: 0.7, textTransform: "uppercase", marginBottom: 8 }}>
                – Removals ({removals.length})
              </p>
              {removals.length === 0
                ? <p style={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>None</p>
                : removals.map(v => (
                  <div key={v.id} style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "8px 11px",
                    borderRadius: 9, border: "1px solid #fca5a5", background: "#fff5f5", marginBottom: 5,
                  }}>
                    <span style={{ fontSize: 14, opacity: 0.4 }}>{vmeta(v.view_name).icon}</span>
                    <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "#dc2626", textDecoration: "line-through" }}>{v.view_name}</span>
                    <button onClick={() => toggleStd(v.id)} title="Restore" style={{ background: "none", border: "none", cursor: "pointer", color: "#fca5a5", fontSize: 14, padding: 2 }}>↩</button>
                  </div>
                ))
              }
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderTop: "1px solid #f1f5f9" }}>
            {[[additions.length, "#16a34a", "Added"], [removals.length, "#dc2626", "Removed"]].map(([n, col, lbl]) => (
              <div key={lbl} style={{ textAlign: "center", padding: "12px 8px", background: "#f8fafc", borderRight: lbl === "Added" ? "1px solid #f1f5f9" : "none" }}>
                <p style={{ fontSize: 20, fontWeight: 800, color: col, margin: 0 }}>{n}</p>
                <p style={{ fontSize: 11, color: "#94a3b8", margin: "2px 0 0" }}>{lbl}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
        <button onClick={onPreview} style={{
          display: "flex", alignItems: "center", gap: 8, padding: "12px 26px",
          borderRadius: 11, border: "none", background: "#0f172a", color: "#fff",
          fontSize: 13, fontWeight: 700, cursor: "pointer",
          boxShadow: "0 4px 14px rgba(15,23,42,0.25)",
        }}>
          Preview & Publish →
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: PREVIEW
// ═══════════════════════════════════════════════════════════════════════════════
function PreviewTab({ selectedPid, selectedGroup, finalViews, additions, removals, onBack, onPublish, publishing }) {
  return (
    <div style={{ maxWidth: 620 }}>
      <SectionHead icon="👁️" title="Preview & Publish" sub="Review the final view list before saving to database." />

      <div style={{ background: "#fff", borderRadius: 16, border: "1.5px solid #e2e8f0", overflow: "hidden", marginBottom: 20 }}>
        <div style={{
          padding: "16px 20px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <p style={{ fontSize: 14, fontWeight: 800, color: "#0f172a", margin: "0 0 3px" }}>Final Dashboard Configuration</p>
            <p style={{ fontSize: 12, color: "#94a3b8", margin: 0 }}>
              <span style={{ fontFamily: "monospace", fontWeight: 700, color: "#475569" }}>{selectedPid}</span>
              &nbsp;· Group <strong style={{ color: "#475569" }}>{selectedGroup}</strong>
            </p>
          </div>
          <Badge color="dark">{finalViews.length} views</Badge>
        </div>

        {finalViews.length === 0
          ? <div style={{ padding: "48px 24px", textAlign: "center" }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
              <p style={{ fontSize: 13, color: "#94a3b8" }}>No views selected.</p>
            </div>
          : <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
              {finalViews.map((v, i) => {
                const isAdded = additions.some(a => a.id === v.id);
                const isStd   = STANDARD_IDS.has(v.id) && !isAdded;
                const meta    = vmeta(v.view_name);
                return (
                  <div key={v.id} style={{
                    display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
                    borderRadius: 11,
                    border: `1px solid ${isAdded ? "#86efac" : "#f1f5f9"}`,
                    background: isAdded ? "#f0fdf4" : "#fff",
                  }}>
                    <span style={{
                      width: 28, height: 28, borderRadius: 9, flexShrink: 0,
                      background: "#0f172a", color: "#fff", display: "flex",
                      alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800,
                    }}>{i + 1}</span>
                    <span style={{
                      width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                      background: meta.accent + "18", display: "flex",
                      alignItems: "center", justifyContent: "center", fontSize: 18,
                    }}>{meta.icon}</span>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", margin: 0 }}>{v.view_name}</p>
                      <p style={{ fontSize: 11, color: "#94a3b8", margin: 0 }}>ID: {v.id}</p>
                    </div>
                    {isAdded && <Badge color="green">✓ Added</Badge>}
                    {isStd   && <Badge color="blue">Standard</Badge>}
                  </div>
                );
              })}
            </div>
        }

        {finalViews.length > 0 && (
          <div style={{
            padding: "10px 20px", borderTop: "1px solid #f1f5f9", background: "#f8fafc",
            display: "flex", gap: 20, fontSize: 12, color: "#64748b", flexWrap: "wrap",
          }}>
            <span>Standard: <strong style={{ color: "#1e293b" }}>{finalViews.filter(v => STANDARD_IDS.has(v.id) && !additions.some(a => a.id === v.id)).length}</strong></span>
            <span>Custom Added: <strong style={{ color: "#16a34a" }}>{additions.length}</strong></span>
            <span>Removed: <strong style={{ color: "#dc2626" }}>{removals.length}</strong></span>
            <span>Total: <strong style={{ color: "#1e293b" }}>{finalViews.length}</strong></span>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={onBack} style={{
          display: "flex", alignItems: "center", gap: 6, padding: "11px 20px",
          borderRadius: 10, border: "1.5px solid #e2e8f0", background: "#fff",
          color: "#475569", fontSize: 13, fontWeight: 700, cursor: "pointer",
        }}>← Back</button>
        <button onClick={onPublish} disabled={publishing || finalViews.length === 0} style={{
          display: "flex", alignItems: "center", gap: 8, padding: "11px 26px",
          borderRadius: 10, border: "none",
          background: publishing || finalViews.length === 0 ? "#e2e8f0" : "#0f172a",
          color: publishing || finalViews.length === 0 ? "#94a3b8" : "#fff",
          fontSize: 13, fontWeight: 700,
          cursor: publishing || finalViews.length === 0 ? "not-allowed" : "pointer",
          boxShadow: finalViews.length > 0 && !publishing ? "0 4px 14px rgba(15,23,42,0.25)" : "none",
        }}>
          {publishing
            ? <><span style={{ display: "inline-block", animation: "spin 1s linear infinite" }}>⟳</span> Publishing…</>
            : <>🚀 Publish Dashboard</>}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: MANAGED GROUPS
// ═══════════════════════════════════════════════════════════════════════════════

// ─── Role IDs pill editor ──────────────────────────────────────────────────────
function RoleIdsPillEditor({ roleIds: rawRoleIds, onChange }) {
  const roleIds = normalizeRoleIds(rawRoleIds);
  return (
    <div>
      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 6 }}>
        Role IDs (1–50)
      </label>
      <MultiSelect
        options={ROLE_OPTIONS}
        selected={roleIds}
        onChange={onChange}
        placeholder="Select role IDs…"
      />
      {roleIds.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 10 }}>
          {[...roleIds].sort((a, b) => a - b).map(rid => (
            <span key={rid} style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 700,
              background: "#e0e7ff", color: "#3730a3",
            }}>
              R{rid}
              <button
                onClick={() => onChange(roleIds.filter(r => r !== rid))}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#818cf8", fontSize: 11, padding: 0, lineHeight: 1 }}
              >✕</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Inline Edit Row ─────────────────────────────────────────────────────────
function GroupEditRow({ group, onSave, onCancel, saving }) {
  const [name,    setName]    = useState(group.group_name);
  const [roleIds, setRoleIds] = useState(normalizeRoleIds(group.role_ids));

  return (
    <div style={{
      background: "#f0f9ff", border: "1.5px solid #bae6fd",
      borderRadius: 14, overflow: "hidden",
    }}>
      {/* mini header */}
      <div style={{ padding: "10px 18px", background: "#e0f2fe", borderBottom: "1px solid #bae6fd", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 14 }}>✏️</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: "#0369a1" }}>Editing: {group.group_name}</span>
        <Badge color="blue">PID: {group.pid}</Badge>
      </div>
      <div style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 6 }}>
            Group Name *
          </label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            style={{
              width: "100%", padding: "10px 13px", borderRadius: 9,
              border: "1.5px solid #bae6fd", fontSize: 13, fontWeight: 500, color: "#0f172a",
              background: "#fff", outline: "none", boxSizing: "border-box",
            }}
            onFocus={e => e.target.style.borderColor = "#0369a1"}
            onBlur={e => e.target.style.borderColor = "#bae6fd"}
          />
        </div>
        <RoleIdsPillEditor roleIds={roleIds} onChange={setRoleIds} />
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => onSave({ group_name: name.trim(), role_ids: roleIds })}
            disabled={saving || !name.trim()}
            style={{
              display: "flex", alignItems: "center", gap: 7, padding: "9px 20px",
              borderRadius: 9, border: "none",
              background: saving || !name.trim() ? "#e2e8f0" : "#0f172a",
              color: saving || !name.trim() ? "#94a3b8" : "#fff",
              fontSize: 13, fontWeight: 700, cursor: saving || !name.trim() ? "not-allowed" : "pointer",
            }}>
            {saving ? <><span style={{ display: "inline-block", animation: "spin 1s linear infinite" }}>⟳</span> Saving…</> : "💾 Save Changes"}
          </button>
          <button onClick={onCancel} style={{
            padding: "9px 16px", borderRadius: 9, border: "1.5px solid #e2e8f0",
            background: "#fff", color: "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Group Card ───────────────────────────────────────────────────────────────
function GroupCard({ group, onEdit, onDelete, deleting }) {
  const roleIds = normalizeRoleIds(group.role_ids);
  return (
    <div style={{
      background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0",
      overflow: "hidden", transition: "box-shadow .15s",
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.07)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px" }}>
        <div style={{
          width: 40, height: 40, borderRadius: 11, flexShrink: 0,
          background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20,
        }}>👥</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>{group.group_name}</span>
            <Badge color="teal">{roleIds.length} roles</Badge>
          </div>
          {roleIds.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {roleIds.slice(0, 10).map(rid => (
                <span key={rid} style={{
                  padding: "1px 7px", borderRadius: 20, fontSize: 10, fontWeight: 700,
                  background: "#e0e7ff", color: "#3730a3",
                }}>R{rid}</span>
              ))}
              {roleIds.length > 10 && (
                <span style={{ padding: "1px 7px", borderRadius: 20, fontSize: 10, fontWeight: 700, background: "#f1f5f9", color: "#64748b" }}>
                  +{roleIds.length - 10} more
                </span>
              )}
            </div>
          ) : (
            <span style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>No roles assigned</span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <button onClick={onEdit} style={{
            padding: "6px 14px", borderRadius: 8, border: "1.5px solid #e2e8f0",
            background: "#fff", color: "#475569", fontSize: 12, fontWeight: 700, cursor: "pointer",
          }}>✏️ Edit</button>
          <button onClick={onDelete} disabled={deleting} style={{
            padding: "6px 12px", borderRadius: 8, border: "1.5px solid #fecdd3",
            background: "#fff5f5", color: "#dc2626", fontSize: 12, fontWeight: 700,
            cursor: deleting ? "not-allowed" : "pointer", opacity: deleting ? 0.6 : 1,
          }}>
            {deleting ? "⟳" : "🗑"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Create Group Form ─────────────────────────────────────────────────────────
function CreateGroupForm({ selectedPid, onSave, onCancel, saving }) {
  const [name,    setName]    = useState("");
  const [roleIds, setRoleIds] = useState([]);

  return (
    <div style={{
      background: "#f8faff", border: "1.5px solid #c7d2fe",
      borderRadius: 14, padding: "18px 20px", marginBottom: 12,
      animation: "fadeIn .2s ease",
    }}>
      <p style={{ fontSize: 13, fontWeight: 800, color: "#4338ca", margin: "0 0 4px" }}>✨ Create New Group</p>
      <p style={{ fontSize: 11, color: "#94a3b8", margin: "0 0 16px" }}>
        Will be created under PID: <span style={{ fontFamily: "monospace", fontWeight: 700, color: "#475569" }}>{selectedPid}</span>
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 6 }}>
            Group Name *
          </label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Partners, Delivery, Admins…"
            style={{
              width: "100%", padding: "10px 13px", borderRadius: 9,
              border: "1.5px solid #c7d2fe", fontSize: 13, fontWeight: 500, color: "#0f172a",
              background: "#fff", outline: "none", boxSizing: "border-box",
            }}
            onFocus={e => e.target.style.borderColor = "#6366f1"}
            onBlur={e => e.target.style.borderColor = "#c7d2fe"}
          />
        </div>
        <RoleIdsPillEditor roleIds={roleIds} onChange={setRoleIds} />
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => onSave({ group_name: name.trim(), role_ids: roleIds })}
            disabled={saving || !name.trim()}
            style={{
              display: "flex", alignItems: "center", gap: 7, padding: "9px 20px",
              borderRadius: 9, border: "none",
              background: saving || !name.trim() ? "#e2e8f0" : "#0f172a",
              color: saving || !name.trim() ? "#94a3b8" : "#fff",
              fontSize: 13, fontWeight: 700, cursor: saving || !name.trim() ? "not-allowed" : "pointer",
            }}>
            {saving ? <><span style={{ display: "inline-block", animation: "spin 1s linear infinite" }}>⟳</span> Creating…</> : "✓ Create Group"}
          </button>
          <button onClick={onCancel} style={{
            padding: "9px 16px", borderRadius: 9, border: "1.5px solid #e2e8f0",
            background: "#fff", color: "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Managed Groups Tab ───────────────────────────────────────────────────────
function ManagedGroupsTab({ pids, showToast }) {
  const [selectedPid,   setSelectedPid]   = useState("");
  const [groups,        setGroups]        = useState([]);
  const [loadingGrps,   setLoadingGrps]   = useState(false);

  const [showCreate,    setShowCreate]    = useState(false);
  // Track editing by group_name (unique per pid) — NOT pid alone (pid has duplicates)
  const [editingName,   setEditingName]   = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [deletingName,  setDeletingName]  = useState(null);

  // Load groups whenever selected PID changes
  useEffect(() => {
    if (!selectedPid) { setGroups([]); return; }
    setLoadingGrps(true);
    setShowCreate(false);
    setEditingName(null);
    apiFetch(`/api/groups/?pid=${encodeURIComponent(selectedPid)}`)
      .then(d => setGroups(d.groups || []))
      .catch(e => showToast("Could not load groups: " + e.message, "error"))
      .finally(() => setLoadingGrps(false));
  }, [selectedPid]);

  async function handleCreate({ group_name, role_ids }) {
    setSaving(true);
    try {
      const data = await apiFetch("/api/groups/", {
        method: "POST",
        body: JSON.stringify({ pid: selectedPid, group_name, role_ids }),
      });
      setGroups(prev => [...prev, data]);
      setShowCreate(false);
      showToast(`Group "${data.group_name}" created! 🎉`);
    } catch (e) {
      showToast("Create failed: " + e.message, "error");
    }
    setSaving(false);
  }

  async function handleSaveEdit(originalGroupName, { group_name, role_ids }) {
    setSaving(true);
    try {
      // POST body to /api/groups/update/ — no URL slash issues at all
      const data = await apiFetch("/api/groups/update/", {
        method: "POST",
        body: JSON.stringify({
          pid:        selectedPid,
          group_name: originalGroupName,   // lookup key
          role_ids,
          ...(group_name !== originalGroupName ? { new_group_name: group_name } : {}),
        }),
      });
      setGroups(prev => prev.map(g =>
        g.pid === selectedPid && g.group_name === originalGroupName ? data : g
      ));
      setEditingName(null);
      showToast(`Group "${data.group_name}" updated ✓`);
    } catch (e) {
      showToast("Update failed: " + e.message, "error");
    }
    setSaving(false);
  }

  async function handleDelete(groupName) {
    if (!window.confirm(`Delete group "${groupName}"? This will not remove saved access configs.`)) return;
    setDeletingName(groupName);
    try {
      // POST body to /api/groups/delete/ — deletes ONLY this exact (pid, group_name) row
      await apiFetch("/api/groups/delete/", {
        method: "POST",
        body: JSON.stringify({ pid: selectedPid, group_name: groupName }),
      });
      setGroups(prev => prev.filter(g => !(g.pid === selectedPid && g.group_name === groupName)));
      showToast(`Group "${groupName}" deleted`);
    } catch (e) {
      showToast("Delete failed: " + e.message, "error");
    }
    setDeletingName(null);
  }

  return (
    <div style={{ maxWidth: 900 }}>
      <SectionHead
        icon="🗂️"
        title="Managed Groups"
        sub="Select a PID to view, edit and manage its role groups."
        right={
          selectedPid && (
            <button
              onClick={() => { setShowCreate(true); setEditingName(null); }}
              style={{
                display: "flex", alignItems: "center", gap: 7, padding: "9px 18px",
                borderRadius: 10, border: "none", background: "#0f172a", color: "#fff",
                fontSize: 13, fontWeight: 700, cursor: "pointer",
                boxShadow: "0 2px 8px rgba(15,23,42,0.18)",
              }}>
              + New Group
            </button>
          )
        }
      />

      {/* PID selector */}
      <div style={{
        background: "#fff", borderRadius: 12, border: "1.5px solid #e2e8f0",
        padding: "16px 18px", marginBottom: 20,
      }}>
        <div style={{ maxWidth: 380 }}>
          <PidSelect
            label="Select PID to manage groups"
            options={pids}
            value={selectedPid}
            onChange={pid => { setSelectedPid(pid); setShowCreate(false); setEditingName(null); }}
          />
        </div>
        {selectedPid && !loadingGrps && (
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
            <Badge color="dark">{selectedPid}</Badge>
            <Badge color="teal">{groups.length} group{groups.length !== 1 ? "s" : ""}</Badge>
          </div>
        )}
      </div>

      {/* No PID selected */}
      {!selectedPid && (
        <div style={{
          background: "#fff", borderRadius: 16, border: "1.5px dashed #cbd5e1",
          padding: "52px 24px", textAlign: "center",
        }}>
          <div style={{ fontSize: 40, marginBottom: 14 }}>🗂️</div>
          <p style={{ fontSize: 15, fontWeight: 700, color: "#334155", margin: "0 0 6px" }}>Select a PID to get started</p>
          <p style={{ fontSize: 13, color: "#94a3b8" }}>Choose a project above to view and manage its groups.</p>
        </div>
      )}

      {/* Loading */}
      {selectedPid && loadingGrps && (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: 13 }}>
          <span style={{ display: "inline-block", animation: "spin 1s linear infinite", fontSize: 20 }}>⟳</span>
          <p style={{ marginTop: 10 }}>Loading groups…</p>
        </div>
      )}

      {/* Content */}
      {selectedPid && !loadingGrps && (
        <>
          {/* Create form */}
          {showCreate && (
            <CreateGroupForm
              selectedPid={selectedPid}
              onSave={handleCreate}
              onCancel={() => setShowCreate(false)}
              saving={saving}
            />
          )}

          {/* Empty state */}
          {groups.length === 0 && !showCreate && (
            <div style={{
              background: "#fff", borderRadius: 16, border: "1.5px dashed #cbd5e1",
              padding: "52px 24px", textAlign: "center",
            }}>
              <div style={{ fontSize: 40, marginBottom: 14 }}>👥</div>
              <p style={{ fontSize: 15, fontWeight: 700, color: "#334155", margin: "0 0 6px" }}>No groups for {selectedPid}</p>
              <p style={{ fontSize: 13, color: "#94a3b8", margin: "0 0 20px" }}>
                Create the first group to start managing dashboard access for this project.
              </p>
              <button
                onClick={() => setShowCreate(true)}
                style={{ padding: "10px 22px", borderRadius: 10, border: "none", background: "#0f172a", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                + Create First Group
              </button>
            </div>
          )}

          {/* Groups list */}
          {groups.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {groups.map(g =>
                editingName === g.group_name ? (
                  <GroupEditRow
                    key={g.group_name}
                    group={g}
                    onSave={payload => handleSaveEdit(g.group_name, payload)}
                    onCancel={() => setEditingName(null)}
                    saving={saving}
                  />
                ) : (
                  <GroupCard
                    key={g.group_name}
                    group={g}
                    onEdit={() => { setEditingName(g.group_name); setShowCreate(false); }}
                    onDelete={() => handleDelete(g.group_name)}
                    deleting={deletingName === g.group_name}
                  />
                )
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT APP
// ═══════════════════════════════════════════════════════════════════════════════
export default function Project360AccessManagement() {
  const [tab, setTab]           = useState("home");
  const [pids, setPids]         = useState([]);
  const [allViews, setAllViews] = useState([]);
  const [groups, setGroups]     = useState([]);   // group name strings for dropdowns

  // Home tab selection — pid as string, group as array for MultiSelect
  const [selectedPid,    setSelectedPid]    = useState("");
  const [selGroups,      setSelGroups]      = useState([]);

  // config state
  const [existingConfig, setExistingConfig] = useState(null);
  const [configLoaded,   setConfigLoaded]   = useState(false);

  // view toggles
  const [selStdIds, setSelStdIds] = useState([...STANDARD_IDS]);
  const [selCstIds, setSelCstIds] = useState([]);

  // ui
  const [loading,    setLoading]    = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [toast,      setToast]      = useState(null);

  // derived
  const standardViews = allViews.filter(v => STANDARD_IDS.has(v.id));
  const customViews   = allViews.filter(v => !STANDARD_IDS.has(v.id));
  const removals      = standardViews.filter(v => !selStdIds.includes(v.id));
  const additions     = allViews.filter(v => selCstIds.includes(v.id));
  const finalViews    = [
    ...standardViews.filter(v => selStdIds.includes(v.id)),
    ...allViews.filter(v => selCstIds.includes(v.id)),
  ];

  const selectedGroup = selGroups[0] ?? "";

  useEffect(() => {
    apiFetch("/api/dashboard-views/").then(d => setAllViews(d.views || [])).catch(console.error);
    apiFetch("/api/pids/").then(d => setPids(d.pids || [])).catch(console.error);
    apiFetch("/api/group-names/").then(d => setGroups(d.groups || [])).catch(console.error);
  }, []);

  // When PID changes on home tab, reload group names filtered to that PID
  useEffect(() => {
    setConfigLoaded(false);
    setExistingConfig(null);
    if (selectedPid) {
      apiFetch(`/api/group-names/?pid=${encodeURIComponent(selectedPid)}`)
        .then(d => setGroups(d.groups || []))
        .catch(console.error);
    } else {
      apiFetch("/api/group-names/").then(d => setGroups(d.groups || [])).catch(console.error);
    }
  }, [selectedPid]);

  // Reset config when group changes
  useEffect(() => {
    setConfigLoaded(false);
    setExistingConfig(null);
  }, [selGroups]);

  const showToast = useCallback((msg, type = "success") => setToast({ msg, type }), []);

  async function loadConfiguration() {
    if (!selectedPid || !selectedGroup) return;
    setLoading(true);
    try {
      const data    = await apiFetch(`/api/access-config/?pid=${selectedPid}&group=${encodeURIComponent(selectedGroup)}`);
      const results = data.results || [];

      if (results.length > 0) {
        const cfg = results[0];
        setExistingConfig(cfg);
        const stdIds = cfg.standard_view_ids?.length ? cfg.standard_view_ids : [...STANDARD_IDS];
        setSelStdIds(stdIds);
        setSelCstIds(cfg.custom_view_ids || []);
        showToast("Configuration loaded ✓");
      } else {
        setExistingConfig(null);
        setSelStdIds([...STANDARD_IDS]);
        setSelCstIds([]);
        showToast("No existing config — starting fresh");
      }
      setConfigLoaded(true);
    } catch (e) {
      showToast("Load failed: " + e.message, "error");
    }
    setLoading(false);
  }

  async function publishConfig() {
    setPublishing(true);
    try {
      const data = await apiFetch("/api/access-config/upsert/", {
        method: "POST",
        body: JSON.stringify({
          pid:               selectedPid,
          created_groups:    selectedGroup,
          standard_view_ids: selStdIds,
          custom_view_ids:   selCstIds,
        }),
      });
      setExistingConfig(data);
      setConfigLoaded(true);
      showToast(data.created ? "Configuration created! 🎉" : "Configuration updated! 🎉");
      setTab("home");
    } catch (e) {
      showToast("Publish failed: " + e.message, "error");
    }
    setPublishing(false);
  }

  const toggleStd = id => setSelStdIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleCst = id => setSelCstIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const TABS = [
    { id: "home",           label: "Home",           icon: "🏠" },
    { id: "configure",      label: "Configure",      icon: "⚙️" },
    { id: "preview",        label: "Preview",        icon: "👁️" },
    { id: "managed_groups", label: "Managed Groups", icon: "🗂️" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#f1f5f9", fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes toastIn { from { opacity: 0; transform: translateY(10px) scale(.97); } to { opacity: 1; transform: none; } }
        @keyframes fadeIn  { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 8px; }
        button:focus-visible { outline: 2px solid #3b82f6; outline-offset: 2px; }
      `}</style>

      {/* ── Top navbar ── */}
      <nav style={{
        background: "#fff", borderBottom: "1px solid #e2e8f0",
        position: "sticky", top: 0, zIndex: 30,
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}>
        <div style={{
          maxWidth: 1180, margin: "0 auto", padding: "0 24px",
          display: "flex", alignItems: "center", justifyContent: "space-between", height: 54,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 9, background: "#0f172a",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
            }}>🧲</div>
            <span style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>Dashboard Magnet</span>
            <span style={{ color: "#cbd5e1", fontSize: 13 }}>/</span>
            <span style={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>Admin Portal</span>
          </div>
          {configLoaded && selectedPid && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Badge color="dark">{selectedPid}</Badge>
              <Badge color="teal">Group: {selectedGroup}</Badge>
              {existingConfig && <Badge color="green">Config Loaded ✓</Badge>}
            </div>
          )}
        </div>
      </nav>

      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "28px 24px" }}>

        {/* ── Tab bar ── */}
        <div style={{
          display: "inline-flex", gap: 3, background: "#fff",
          border: "1.5px solid #e2e8f0", borderRadius: 14, padding: 4,
          marginBottom: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        }}>
          {TABS.map(t => {
            const needsConfig = t.id === "configure" || t.id === "preview";
            const disabled    = needsConfig && !configLoaded;
            const active      = tab === t.id;
            return (
              <button key={t.id}
                onClick={() => !disabled && setTab(t.id)}
                disabled={disabled}
                style={{
                  display: "flex", alignItems: "center", gap: 7,
                  padding: "9px 20px", borderRadius: 10, border: "none",
                  fontSize: 13, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
                  background: active ? "#0f172a" : "transparent",
                  color: active ? "#fff" : disabled ? "#cbd5e1" : "#64748b",
                  transition: "all .15s",
                }}>
                <span style={{ fontSize: 15 }}>{t.icon}</span>{t.label}
              </button>
            );
          })}
        </div>

        {/* ── Content ── */}
        {tab === "home" && (
          <HomeTab
            pids={pids}
            groups={groups}
            selectedPid={selectedPid}
            setSelectedPid={setSelectedPid}
            selectedGroups={selGroups}
            setSelectedGroups={setSelGroups}
            configLoaded={configLoaded}
            loading={loading}
            onLoad={loadConfiguration}
            existingConfig={existingConfig}
            allViews={allViews}
            onEdit={() => setTab("configure")}
          />
        )}

        {tab === "configure" && configLoaded && (
          <ConfigureTab
            selectedPid={selectedPid}
            selectedGroup={selectedGroup}
            standardViews={standardViews}
            customViews={customViews}
            selStdIds={selStdIds}
            selCstIds={selCstIds}
            toggleStd={toggleStd}
            toggleCst={toggleCst}
            additions={additions}
            removals={removals}
            finalViews={finalViews}
            onPreview={() => setTab("preview")}
          />
        )}

        {tab === "preview" && configLoaded && (
          <PreviewTab
            selectedPid={selectedPid}
            selectedGroup={selectedGroup}
            finalViews={finalViews}
            additions={additions}
            removals={removals}
            onBack={() => setTab("configure")}
            onPublish={publishConfig}
            publishing={publishing}
          />
        )}

        {tab === "managed_groups" && (
          <ManagedGroupsTab
            pids={pids}
            showToast={showToast}
          />
        )}
      </div>

      {toast && (
        <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
}